import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Home as RoomIcon,
  LocationOn as LocationIcon,
  People as CapacityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { Room, Location, ClassCategory } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const RoomsPage: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [classCategories, setClassCategories] = useState<ClassCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    capacity: '',
    description: '',
    supported_classcategory_ids: [] as string[],
  });

  const loadRooms = useCallback(async () => {
    try {
      setLoading(true);
      const [roomsData, locationsData, categoriesData] = await Promise.all([
        JuntoApiService.getRooms(includeInactive),
        JuntoApiService.getLocations(includeInactive),
        JuntoApiService.getClassCategories(includeInactive),
      ]);
      setRooms(roomsData);
      setLocations(locationsData);
      setClassCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const getLocationName = (locationId: string): string => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  const getCategoryName = (categoryId: string): string => {
    const category = classCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const openCreateDialog = () => {
    setEditingRoom(null);
    setFormData({
      name: '',
      location_id: '',
      capacity: '',
      description: '',
      supported_classcategory_ids: [],
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      location_id: room.location_id,
      capacity: room.capacity.toString(),
      description: room.description || '',
      supported_classcategory_ids: room.supported_classcategory_ids,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const roomData = {
        ...formData,
        id: editingRoom?.id || `room-${Date.now()}`,
        capacity: parseInt(formData.capacity),
        created_at: editingRoom?.created_at || new Date().toISOString(),
      };

      if (editingRoom) {
        // Update existing room
        await JuntoApiService.updateRoom(roomData as Room);
      } else {
        // Create new room
        await JuntoApiService.createRoom(roomData);
      }
      
      await loadRooms();
      closeDialog();
    } catch (err) {
      console.error('Failed to save room:', err);
      setDialogError('Failed to save room. Please check your input.');
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading rooms...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Rooms</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Room
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadRooms} sx={{ ml: 2 }}>Retry</Button>
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Supported Categories</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow 
                  key={room.id} 
                  hover
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <RoomIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="body1" fontWeight="medium">
                        {room.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationIcon color="action" sx={{ mr: 1 }} />
                      {getLocationName(room.location_id)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <CapacityIcon color="action" sx={{ mr: 1 }} />
                      {room.capacity} people
                    </Box>
                  </TableCell>
                  <TableCell>
                    {room.supported_classcategory_ids.map((catId) => (
                      <Chip 
                        key={catId} 
                        label={getCategoryName(catId)} 
                        size="small" 
                        variant="outlined" 
                        sx={{ mr: 0.5 }} 
                      />
                    ))}
                  </TableCell>
                  <TableCell>{room.description || 'N/A'}</TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={room}
                      entityType="rooms"
                      entityDisplayName="Room"
                      onEdit={openEditDialog}
                      onRefresh={loadRooms}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Location:</strong> {getLocationName(entity.location_id)}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Capacity:</strong> {entity.capacity} people
                          </Typography>
                          {entity.description && (
                            <Typography variant="body1">
                              <strong>Description:</strong> {entity.description}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRoom ? 'Edit Room' : 'Create New Room'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Supported Class Categories</InputLabel>
                <Select
                  multiple
                  value={formData.supported_classcategory_ids}
                  onChange={(e) => setFormData({ ...formData, supported_classcategory_ids: e.target.value as string[] })}
                  input={<OutlinedInput label="Supported Class Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((categoryId) => (
                        <Chip key={categoryId} label={getCategoryName(categoryId)} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {classCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={formData.supported_classcategory_ids.indexOf(category.id) > -1} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingRoom ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomsPage;